"use client"

import { useRef } from 'react'
import Image from 'next/image'
import { motion, useScroll, useTransform } from 'framer-motion'
import styles from './TimelineGuide.module.css'
import type { ReactNode } from 'react'


export interface TimelineStepData {
  id: number | string;
  title: string;
  description: ReactNode;
  image?: string;
  images?: string[];
  layout: 'left' | 'right';
  link?: { text: string; href: string };
  showBadge?: boolean;
}

interface TimelineGuideProps {
  steps: TimelineStepData[];
}

const TimelineStep = ({ step, index }: { step: TimelineStepData; index: number }) => {
  const stepRef = useRef<HTMLDivElement>(null)

  const { scrollYProgress } = useScroll({
    target: stepRef,
    offset: ["start 60%", "start center"],
  })

  const opacity = useTransform(scrollYProgress, [0, 1], [0.2, 1])
  const filter = useTransform(scrollYProgress, [0, 1], ["grayscale(100%)", "grayscale(0%)"])

  const hasDualImages = step.images && step.images.length >= 2
  const showBadge = step.showBadge !== false // defaults to true if not specified

  return (
    <div ref={stepRef} className={`${styles.step} ${styles[step.layout]}`}>
      <motion.div className={styles.stepContent} style={{ opacity, filter }}>
        <h3 className={styles.stepTitle}>{step.title}</h3>
        <p className={styles.stepDescription}>
          {step.description}{' '}
          {step.link && (
            <a href={step.link.href} target="_blank" rel="noopener noreferrer" className={styles.stepLink}>
              {step.link.text}
            </a>
          )}
        </p>
      </motion.div>

      <div className={showBadge ? styles.stepNumberShield : styles.stepNumberShieldHidden}>
        {showBadge && (
          <motion.div className={styles.stepNumber} style={{ opacity, filter }}>
            <span>{String(step.id).padStart(2, '0')}</span>
          </motion.div>
        )}
      </div>

      {hasDualImages ? (
        <motion.div className={styles.stepDualImages} style={{ opacity, filter }}>
          {step.images!.map((src, idx) => (
            <div key={idx} className={styles.stepImage}>
              <Image
                src={src}
                alt={`Step ${step.id} image ${idx + 1}`}
                fill
                className={styles.image}
              />
            </div>
          ))}
        </motion.div>
      ) : (
        <motion.div className={styles.stepImage} style={{ opacity, filter }}>
          <Image
            src={step.image!}
            alt={`Step ${step.id}`}
            fill
            className={styles.image}
          />
        </motion.div>
      )}
    </div>
  )
}

export default function TimelineGuide({ steps }: TimelineGuideProps) {
  const timelineRef = useRef<HTMLDivElement>(null)

  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start center", "end center"],
  })

  return (
    <div className={styles.timeline} ref={timelineRef}>
      <div className={styles.lineBackground}></div>
      <motion.div
        className={styles.lineProgress}
        style={{ scaleY: scrollYProgress }}
      />
      {steps.map((step, index) => (
        <TimelineStep key={`${step.id}-${index}`} step={step} index={index} />
      ))}
    </div>
  )
}